import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide6aMobile from "./HTLDSlide6aMobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide6aContent, useSlide6a, Seg } from "./slide6a.data";

const accent    = "rgb(212, 83, 126)";
const accentRaw = "212, 83, 126";
const font      = FONT_BODY;
const mono      = FONT_MONO;

const S: Record<string, CSSProperties> = {
root: {
background: "#040f18", fontFamily: font, color: "#d0e8f8",
width: "100%", height: "100%",
display: "flex", flexDirection: "column",
boxSizing: "border-box", overflow: "hidden",
},
// Shared header row: breadcrumb (left) + slide title (right), above the divider line
subtitleBar: {
background: "#050f1a",
borderBottom: `1px solid rgba(${accentRaw},0.10)`,
padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
display: "flex", alignItems: "center", justifyContent: "space-between",
gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body: {
flex: 1, minHeight: 0,
display: "flex", gap: "clamp(16px,2vw,28px)",
padding: "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)",
alignItems: "stretch", overflow: "hidden",
},
content: {
flex: 1, minWidth: "min(100%, 380px)",
display: "flex", flexDirection: "column",
justifyContent: "space-between",
},
panel: {
width: "clamp(220px,28vw,380px)", flexShrink: 0,
display: "flex", flexDirection: "column",
overflow: "hidden",
},
};

// ─── Hex Grid Canvas ──────────────────────────────────────────────────────────
const HexGridCanvas: React.FC = () => {
const canvasRef = useRef<HTMLCanvasElement>(null);
const drawFnRef = useRef<null | (() => void)>(null);
const dtRef     = useRef<number>(0);

useEffect(() => {
const canvas = canvasRef.current; if (!canvas) return;
const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
const W = 420, H = 560;
const cTxt = pick(slide6aContent).canvas; // slides remount on language change
const dpr = window.devicePixelRatio || 1;
canvas.width  = W * dpr;
canvas.height = H * dpr;
ctx.scale(dpr, dpr);

let frame = 0;

const R  = 28;
const HW = R * Math.sqrt(3);
const HV = R * 1.5;
const GAP = 1.0;
const CORE_X = W / 2, CORE_Y = H * 0.42;

interface HexCell {
  cx: number; cy: number;
  isCore: boolean;
  label?: string;
  nodePhaseOffset: number;
  activation: number;
  distFromCore: number;
}

const cells: HexCell[] = [];
for (let row = 0; row < 15; row++) {
  const xOff = (row % 2 === 1) ? HW / 2 : 0;
  for (let col = 0; col < 10; col++) {
    const cx = col * HW + xOff + HW * 0.5;
    const cy = row * HV + R;
    if (cx >= R * 0.4 && cx <= W - R * 0.4 && cy >= R * 0.4 && cy <= H - R * 0.4)
      cells.push({ cx, cy, isCore: false, activation: 0, distFromCore: 0, nodePhaseOffset: 0 });
  }
}

// Mark 3 core cells nearest to canvas center
const sorted = [...cells].sort((a, b) =>
  Math.hypot(a.cx - CORE_X, a.cy - CORE_Y) - Math.hypot(b.cx - CORE_X, b.cy - CORE_Y)
);
sorted.slice(0, 3).forEach(c => c.isCore = true);

const coreCells   = cells.filter(c => c.isCore);
const coreCenterX = coreCells.reduce((s, c) => s + c.cx, 0) / coreCells.length;
const coreCenterY = coreCells.reduce((s, c) => s + c.cy, 0) / coreCells.length;

cells.forEach(cell => {
  cell.distFromCore = Math.hypot(cell.cx - coreCenterX, cell.cy - coreCenterY) / HW;
});

// ── BFS: compute hop-distance from core cells ─────────────────────────────
const hopDist = new Map<HexCell, number>();
coreCells.forEach(c => hopDist.set(c, 0));
const bfsQ: HexCell[] = [...coreCells];
for (let qi = 0; qi < bfsQ.length; qi++) {
const cur = bfsQ[qi];
const d   = hopDist.get(cur)!;
if (d >= 3) continue;                          // only expand up to depth 3
cells.forEach(nb => {
if (hopDist.has(nb)) return;                 // already visited
if (Math.hypot(nb.cx - cur.cx, nb.cy - cur.cy) < HW * 1.1) {
  hopDist.set(nb, d + 1);
  bfsQ.push(nb);
}
});
}

// ── All cells exactly 3 hops from core = a true equidistant ring ──────────
const ring3 = cells.filter(c => hopDist.get(c) === 3);

// ── Pick 1 cell per 60° sector, closest in angle to each target ───────────
const nodeConf = [
{ angle: -120, label: cTxt.nodes[0], phase: 0.00 },
{ angle:  -60, label: cTxt.nodes[1], phase: 1.05 },
{ angle:    0, label: cTxt.nodes[2], phase: 2.09 },
{ angle:   60, label: cTxt.nodes[3], phase: 3.14 },
{ angle:  120, label: cTxt.nodes[4], phase: 4.19 },
{ angle:  180, label: cTxt.nodes[5], phase: 5.24 },
];
const usedCells = new Set<HexCell>();
nodeConf.forEach(({ angle, label, phase }) => {
const tAngle = angle * Math.PI / 180;
let best: HexCell | null = null, bestDiff = Infinity;
ring3.forEach(c => {
if (usedCells.has(c)) return;
let diff = Math.abs(
  Math.atan2(c.cy - coreCenterY, c.cx - coreCenterX) - tAngle
);
if (diff > Math.PI) diff = 2 * Math.PI - diff;  // wraparound
if (diff < bestDiff) { bestDiff = diff; best = c; }
});
const m = best as HexCell | null;
if (m) { m.label = label; m.nodePhaseOffset = phase; usedCells.add(m); }
});

interface Wave { spawnFrame: number }
const waves: Wave[] = [{ spawnFrame: 0 }];
let nextWaveFrame = 200;
const EXPAND_SPEED = 1 / 20;
const WAVE_WIDTH   = 1.4;

const ambParts = Array.from({ length: 35 }, () => ({
  x: Math.random() * W, y: Math.random() * H,
  vx: (Math.random() - 0.5) * 0.06, vy: (Math.random() - 0.5) * 0.06,
  r: 0.3 + Math.random() * 0.9,
  tw: Math.random() * Math.PI * 2, tws: 0.005 + Math.random() * 0.010,
}));

function drawHex(cx: number, cy: number, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI / 3) - Math.PI / 2;
    i === 0
      ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a))
      : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
  }
  ctx.closePath();
}

function draw() {
  ctx.clearRect(0, 0, W, H);

  // ── 1. Background ─────────────────────────────────────────────────────
  const bg = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W * 0.88);
  bg.addColorStop(0, "#0c1a28"); bg.addColorStop(0.5, "#071320"); bg.addColorStop(1, "#030a12");
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  ([
    [W * 0.18, H * 0.12, 115, `rgba(${accentRaw},0.10)`],
    [W * 0.82, H * 0.14, 105, `rgba(180,50,100,0.07)`],
    [W * 0.10, H * 0.78, 125, `rgba(${accentRaw},0.08)`],
    [W * 0.90, H * 0.80, 110, `rgba(190,60,110,0.06)`],
    [W * 0.50, H * 0.50, 210, `rgba(${accentRaw},0.04)`],
  ] as [number,number,number,string][]).forEach(([nx,ny,nr,nc]) => {
    const g = ctx.createRadialGradient(nx,ny,0,nx,ny,nr);
    g.addColorStop(0,nc); g.addColorStop(1,"rgba(0,0,0,0)");
    ctx.beginPath(); ctx.arc(nx,ny,nr,0,Math.PI*2); ctx.fillStyle=g; ctx.fill();
  });

  ambParts.forEach(p => {
    p.x+=p.vx; p.y+=p.vy; p.tw+=p.tws;
    if(p.x<0||p.x>W) p.vx*=-1; if(p.y<0||p.y>H) p.vy*=-1;
    const a = 0.05 + 0.07 * Math.abs(Math.sin(p.tw));
    ctx.beginPath(); ctx.arc(p.x,p.y,p.r,0,Math.PI*2);
    ctx.fillStyle=`rgba(${accentRaw},${a.toFixed(2)})`; ctx.fill();
  });

  // ── 2. Guide ring — faint circle showing the equidistant node ring ───
  ctx.beginPath(); ctx.arc(coreCenterX, coreCenterY, 150, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(${accentRaw},0.06)`; ctx.lineWidth = 1;
  ctx.setLineDash([4, 10]); ctx.stroke(); ctx.setLineDash([]);

  // ── 3. Spawn waves ────────────────────────────────────────────────────
  if (frame >= nextWaveFrame) {
    waves.push({ spawnFrame: frame });
    if (waves.length > 3) waves.shift();
    nextWaveFrame = frame + 200;
  }

  // ── 4. Update activations ─────────────────────────────────────────────
  const corePhase = Math.sin(frame * 0.035);
  cells.forEach(cell => {
    if (cell.isCore) { cell.activation = 0.85 + 0.15 * corePhase; return; }
    let maxAct = 0;
    waves.forEach(w => {
      const waveR = (frame - w.spawnFrame) * EXPAND_SPEED;
      const dist  = Math.abs(cell.distFromCore - waveR);
      if (dist < WAVE_WIDTH) {
        const intensity = Math.exp(-0.5 * Math.pow(dist / (WAVE_WIDTH * 0.4), 2));
        const falloff   = Math.max(0, 1.0 - cell.distFromCore * 0.060);
        maxAct = Math.max(maxAct, intensity * falloff);
      }
    });
    if (cell.label) {
      const ambient = 0.28 + 0.12 * Math.sin(frame * 0.022 + cell.nodePhaseOffset);
      maxAct = Math.max(maxAct, ambient);
    }
    cell.activation += (maxAct - cell.activation) * 0.10;
    cell.activation  = Math.max(0, Math.min(1, cell.activation));
  });

  // ── 5. Draw cells ─────────────────────────────────────────────────────
  cells.forEach(cell => {
    const a = cell.activation;
    if (a < 0.015 && !cell.isCore) return;

    if (a > 0.10 || cell.isCore) {
      const glowR = R * 2.1;
      const ga    = cell.isCore ? 0.16 + corePhase * 0.06 : a * 0.16;
      const g = ctx.createRadialGradient(cell.cx,cell.cy,0, cell.cx,cell.cy, glowR);
      g.addColorStop(0, `rgba(${accentRaw},${ga.toFixed(3)})`);
      g.addColorStop(1, `rgba(${accentRaw},0)`);
      ctx.beginPath(); ctx.arc(cell.cx,cell.cy, glowR, 0, Math.PI*2);
      ctx.fillStyle = g; ctx.fill();
    }

    drawHex(cell.cx, cell.cy, R - GAP);
    const fillA = cell.isCore
      ? 0.48 + 0.22 * corePhase
      : cell.label
        ? Math.max(0.10, a * 0.55)
        : Math.max(0.025, a * 0.32);
    ctx.fillStyle = `rgba(${accentRaw},${fillA.toFixed(3)})`; ctx.fill();

    const strokeA = cell.isCore
      ? 0.85 + 0.14 * corePhase
      : cell.label
        ? Math.max(0.28, a * 0.88)
        : Math.max(0.08, a * 0.52);
    ctx.strokeStyle = `rgba(${accentRaw},${strokeA.toFixed(3)})`;
    ctx.lineWidth = cell.isCore ? 2.0 : cell.label ? 1.5 : 0.7;
    ctx.stroke();

    if (cell.label) {
      ctx.font = `bold 10px ${mono}`;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillStyle = `white`;
      ctx.fillText(cell.label, cell.cx, cell.cy);
    }
  });

  // ── 6. Core label ─────────────────────────────────────────────────────
  const la = 0.60 + 0.18 * Math.sin(frame * 0.038);
  ctx.font = `bold 12px ${mono}`; ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "white";
  ctx.fillText(cTxt.core, coreCenterX, coreCenterY - R * 2.5);
  ctx.font = `11px ${mono}`;
  ctx.fillStyle = `rgba(220,120,160,${la * 0.70})`;
  ctx.fillText(cTxt.coreSub, coreCenterX, coreCenterY - R * 2.5 + 15);

  // ── 7. Wave ring overlay ──────────────────────────────────────────────
  waves.forEach(w => {
    const waveR     = (frame - w.spawnFrame) * EXPAND_SPEED * HW;
    const waveAlpha = Math.max(0, 0.40 - waveR / (W * 0.78));
    if (waveAlpha > 0.005) {
      ctx.beginPath(); ctx.arc(coreCenterX, coreCenterY, waveR, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${accentRaw},${waveAlpha.toFixed(3)})`;
      ctx.lineWidth = 1.6; ctx.stroke();
    }
  });

  // ── 8. Bottom tagline ─────────────────────────────────────────────────
  ctx.font = `15px 'Space Grotesk',Arial,sans-serif`;
  ctx.fillStyle = "white";
  ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
  ctx.fillText(cTxt.caption, W / 2, H - 16);

  frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
}

drawFnRef.current = draw;
return () => { drawFnRef.current = null; };
}, []);

useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });

return (
<div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
  <canvas
    ref={canvasRef}
    style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
  />
</div>
);
};

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const HTLDSlide6aInner: React.FC = () => {
const { meta, causation, connected, buildup, dreams } = useSlide6a();
const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );
const fontLinkRef = useRef<HTMLLinkElement | null>(null);

useEffect(() => {
const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
if (!existing) {
  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
  document.head.appendChild(link);
  fontLinkRef.current = link;
}
return () => {
  if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
    document.head.removeChild(fontLinkRef.current); fontLinkRef.current = null;
  }
};
}, []);

const lbl: CSSProperties = {
fontSize: "clamp(8px,0.7vw,11px)", fontFamily: mono,
color: accent, letterSpacing: 3, textTransform: "uppercase",
marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal",
};
const txt: CSSProperties = {
fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY,
lineHeight: 1.7, color: "#cde0f2", margin: 0,
};

const rootRef = useRef<HTMLDivElement>(null);

return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
<SideStarField rootRef={rootRef} />   {/* add this line */}

  {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
  <div style={S.subtitleBar}>
    <div style={{
      fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
      fontFamily: FONT_BODY,
    }}>
      {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
      <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
    </div>
    <div style={{
      fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
      color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
      whiteSpace: "nowrap",
    }}>
      {meta.headerTitlePre}<span style={{ color: accent }}>{meta.headerTitleAccent}</span>
    </div>
  </div>

  {/* ── Body ── */}
  <div style={S.body}>

    {/* ── Left content column ── */}
    <div style={S.content}>

      {/* Title */}
      <div>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(19px,2.3vw,30px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2 }}>
          {meta.titlePre}<span style={{ color: accent }}>{meta.titleAccentWord}</span>{meta.titlePost}
        </h1>
        <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.1vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#7aa8cc", margin: 0 }}>
        {meta.subtitle}
        </p>
      </div>

      {/* What is psychosomatic */}
      <div style={{ borderBottom: `1px solid rgba(${accentRaw},0.10)`, paddingBottom: "clamp(6px,0.9vw,12px)" }}>
        <div style={lbl}>{meta.connectedLabel}</div>
        <p style={txt}>{renderSegs(connected, "#fac0d2")}</p>
      </div>

      {/* Root causes */}
      <div style={{ borderBottom: `1px solid rgba(${accentRaw},0.10)`, paddingBottom: "clamp(6px,0.9vw,12px)" }}>
        <div style={lbl}>{meta.buildupLabel}</div>
        <p style={txt}>{renderSegs(buildup, "#fac0d2")}</p>
      </div>

      {/* ── Chain of causation ── */}
      <div>
        <div style={lbl}>{meta.chainLabel}</div>
        <div style={{
          background: "rgba(0,0,0,0.30)",
          border: `1px solid rgba(${accentRaw},0.18)`,
          borderRadius: 6, overflow: "hidden",
        }}>
          {/* Title bar */}
          <div style={{
            background: `rgba(${accentRaw},0.08)`,
            borderBottom: `1px solid rgba(${accentRaw},0.14)`,
            padding: "clamp(3px,0.35vw,5px) clamp(7px,0.8vw,11px)",
            display: "flex", alignItems: "center", gap: 5,
          }}>
            {["rgba(255,100,100,0.55)","rgba(255,200,80,0.55)",`rgba(${accentRaw},0.55)`].map((c,i) => (
              <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: c }} />
            ))}
            <span style={{ fontSize: "clamp(6.5px,0.55vw,9px)", fontFamily: mono, color: `rgba(${accentRaw},0.55)`, letterSpacing: 2, marginLeft: 3 }}>
              {meta.chainBarLabel}
            </span>
          </div>

          {/* Entries — uniform brightness, alpha = 1.0 throughout */}
          <div style={{ padding: "clamp(5px,0.6vw,9px) clamp(7px,0.85vw,12px)", display: "flex", flexDirection: "column", gap: "clamp(4px,0.45vw,7px)" }}>
            {causation.map((c, i) => {
              const alpha = 1.0;
              return (
                <div key={i}>
                  {/* Single row: ▶ 01_ STAGE · description   STATUS ■■■ */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "clamp(4px,0.5vw,7px)" }}>

                    {/* Left: prompt + number + stage + inline desc */}
                    <div style={{ display: "flex", alignItems: "baseline", gap: "clamp(2px,0.3vw,5px)", flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <span style={{ color: `rgba(${accentRaw},${alpha})`, fontSize: "clamp(8px,0.68vw,10.5px)", fontFamily: mono, flexShrink: 0 }}>▶</span>
                      <span style={{ fontSize: "clamp(7px,0.6vw,9.5px)", fontFamily: mono, color: `rgba(${accentRaw},0.55)`, letterSpacing: 1, flexShrink: 0 }}>
                        {String(i + 1).padStart(2, "0")}_
                      </span>
                      <span style={{ fontSize: "clamp(8px,0.68vw,10.5px)", fontFamily: mono, color: `rgba(${accentRaw},${alpha})`, letterSpacing: 1.5, textTransform: "uppercase", flexShrink: 0 }}>
                        {c.stage}
                      </span>
                      <span style={{ fontSize: "clamp(7px,0.6vw,9.5px)", fontFamily: mono, color: `rgba(${accentRaw},0.35)`, flexShrink: 0 }}>·</span>
                      <span style={{
                        fontFamily: FONT_BODY, fontSize: "clamp(8.5px,0.72vw,11px)", fontStyle: "normal",
                        color: `rgba(155,189,214,0.85)`,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        flex: 1, minWidth: 0,
                      }}>
                        {c.desc}
                      </span>
                    </div>

                    {/* Right: status + blocks */}
                    <div style={{ display: "flex", alignItems: "center", gap: "clamp(3px,0.4vw,6px)", flexShrink: 0 }}>
                      <span style={{ fontSize: "clamp(6.5px,0.55vw,9px)", fontFamily: mono, color: `rgba(${accentRaw},0.52)`, letterSpacing: 1 }}>
                        {c.status}
                      </span>
                      <div style={{ display: "flex", gap: 2 }}>
                        {Array.from({ length: 8 }, (_, bi) => (
                          <div key={bi} style={{
                            width: "clamp(5px,0.5vw,7px)", height: 4, borderRadius: 1,
                            background: bi < c.bars
                              ? `rgba(${accentRaw},0.85)`
                              : `rgba(${accentRaw},0.10)`,
                          }} />
                        ))}
                      </div>
                    </div>
                  </div>

                  {i < causation.length - 1 && (
                    <div style={{ height: 1, background: `rgba(${accentRaw},0.07)`, marginTop: "clamp(4px,0.45vw,7px)" }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Callout */}
      <div style={{
        background: `rgba(${accentRaw},0.06)`,
        border: `1px solid rgba(${accentRaw},0.22)`,
        borderRadius: 8,
        padding: "clamp(9px,1.1vw,15px) clamp(11px,1.3vw,18px)",
      }}>
        <div style={lbl}>{meta.dreamsLabel}</div>
        <p style={{ ...txt, color: "#f0c0d0" }}>{renderSegs(dreams, "#ffffff")}</p>
      </div>

    </div>

    {/* ── Right panel — hex grid fills the full panel height ── */}
    <div style={S.panel}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <HexGridCanvas />
      </div>
    </div>

  </div>

</div>
);
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const HTLDSlide6a: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide6aMobile />
    : <ScaleToFit><HTLDSlide6aInner /></ScaleToFit>;
};

export default HTLDSlide6a;